import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  BarChart3, Heart, Users, MessageCircle, Eye, Clock, TrendingUp,
  Smartphone, Calendar, ArrowUpRight, ArrowDownRight, RefreshCw
} from 'lucide-react';
import axios from 'axios';

/* ═══════════════════════════════════════════════
   STYLED COMPONENTS
   ═══════════════════════════════════════════════ */

const Page = styled.div`padding: 20px; display: flex; flex-direction: column; gap: 20px;`;
const Header = styled.div`display: flex; align-items: center; justify-content: space-between; gap: 16px;`;
const Title = styled.h1`font-size: 22px; font-weight: 800; color: var(--text-primary); display: flex; align-items: center; gap: 10px;`;

const StatsGrid = styled.div`display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 12px;`;
const StatCard = styled.div`
  background: var(--card-bg); border: 1px solid var(--card-border); border-radius: 12px;
  padding: 16px; display: flex; flex-direction: column; gap: 6px;
`;
const StatLabel = styled.div`font-size: 10px; font-weight: 700; color: var(--text-muted); text-transform: uppercase; display: flex; align-items: center; gap: 6px;`;
const StatValue = styled.div`font-size: 24px; font-weight: 900; color: ${p => p.$color || 'var(--text-primary)'};`;
const StatTrend = styled.div`
  font-size: 11px; font-weight: 600; display: flex; align-items: center; gap: 4px;
  color: ${p => p.$up ? '#22c55e' : '#ef4444'};
`;
const StatSub = styled.div`font-size: 10px; color: var(--text-muted);`;

const SectionTitle = styled.h2`
  font-size: 15px; font-weight: 700; color: var(--text-primary);
  display: flex; align-items: center; gap: 8px; margin: 0;
  padding-bottom: 8px; border-bottom: 1px solid var(--border);
`;

const Table = styled.div`
  background: var(--card-bg); border: 1px solid var(--card-border); border-radius: 12px; overflow: hidden;
`;
const TRow = styled.div`
  display: grid; grid-template-columns: 50px 1fr 100px 100px 100px 120px;
  padding: 10px 16px; align-items: center; gap: 8px;
  border-bottom: 1px solid var(--border); font-size: 12px; color: var(--text-secondary);
  &:last-child { border-bottom: none; }
  &:first-child { font-weight: 700; color: var(--text-muted); text-transform: uppercase; font-size: 10px; background: var(--surface); }
`;

const PeriodBtns = styled.div`display: flex; gap: 6px;`;
const PeriodBtn = styled.button`
  padding: 6px 14px; border-radius: 8px; border: 1px solid ${p => p.$active ? 'var(--primary)' : 'var(--border)'};
  background: ${p => p.$active ? 'var(--primary)' : 'var(--surface)'};
  color: ${p => p.$active ? '#fff' : 'var(--text-secondary)'};
  font-size: 11px; font-weight: 600; cursor: pointer;
`;

const EmptyState = styled.div`
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  min-height: 40vh; color: var(--text-muted); text-align: center;
  svg { opacity: .2; margin-bottom: 12px; }
`;

const RefreshBtn = styled.button`
  padding: 8px 14px; border-radius: 8px; border: 1px solid var(--border);
  background: var(--surface); color: var(--text-secondary); font-size: 12px; font-weight: 600;
  cursor: pointer; display: flex; align-items: center; gap: 6px;
  &:hover { border-color: var(--primary); color: var(--primary); }
`;

/* ═══════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════ */

const TikTokAnalytics = ({ selectedDevice }) => {
  const [period, setPeriod] = useState('7d');
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchStats = async () => {
    if (!selectedDevice?.device_id) return;
    setLoading(true);
    try {
      const r = await axios.get(`/api/tiktok-scripts/analytics/${selectedDevice.device_id}?period=${period}`);
      if (r.data.success) {
        setStats(r.data.stats);
        setSessions(r.data.sessions || []);
      }
    } catch { /* no data yet */ }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchStats(); }, [selectedDevice?.device_id, period]);

  if (!selectedDevice) {
    return <Page><EmptyState><Smartphone size={48} /><p>Sélectionnez un appareil</p></EmptyState></Page>;
  }

  const s = stats || {};
  const trend = (val, prev) => {
    if (!prev || prev === 0) return null;
    const pct = ((val - prev) / prev * 100).toFixed(0);
    return { up: val >= prev, pct: Math.abs(pct) };
  };

  return (
    <Page>
      <Header>
        <Title><BarChart3 size={22} style={{ color: '#ff0050' }} /> TikTok — Analytique</Title>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <PeriodBtns>
            {['24h', '7d', '30d', 'all'].map(p => (
              <PeriodBtn key={p} $active={period === p} onClick={() => setPeriod(p)}>{p}</PeriodBtn>
            ))}
          </PeriodBtns>
          <RefreshBtn onClick={fetchStats} disabled={loading}>
            <RefreshCw size={12} style={loading ? { animation: 'spin 1s linear infinite' } : {}} />
          </RefreshBtn>
        </div>
      </Header>

      <StatsGrid>
        <StatCard>
          <StatLabel><Heart size={12} /> Likes donnés</StatLabel>
          <StatValue $color="#ff0050">{s.total_likes || 0}</StatValue>
          {s.prev_likes != null && trend(s.total_likes || 0, s.prev_likes) && (
            <StatTrend $up={trend(s.total_likes, s.prev_likes).up}>
              {trend(s.total_likes, s.prev_likes).up ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
              {trend(s.total_likes, s.prev_likes).pct}%
            </StatTrend>
          )}
        </StatCard>
        <StatCard>
          <StatLabel><Users size={12} /> Follows</StatLabel>
          <StatValue $color="#00f2ea">{s.total_follows || 0}</StatValue>
          <StatSub>unfollows: {s.total_unfollows || 0}</StatSub>
        </StatCard>
        <StatCard>
          <StatLabel><MessageCircle size={12} /> Commentaires</StatLabel>
          <StatValue $color="#25f4ee">{s.total_comments || 0}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel><Eye size={12} /> Vidéos regardées</StatLabel>
          <StatValue>{s.total_views || 0}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel><Clock size={12} /> Sessions</StatLabel>
          <StatValue>{s.total_sessions || 0}</StatValue>
          <StatSub>durée moy: {s.avg_session_mins || 0}min</StatSub>
        </StatCard>
        <StatCard>
          <StatLabel><TrendingUp size={12} /> Engagement rate</StatLabel>
          <StatValue $color="var(--primary)">{s.engagement_rate || '0'}%</StatValue>
        </StatCard>
      </StatsGrid>

      <SectionTitle><Calendar size={16} /> Historique des sessions</SectionTitle>
      <Table>
        <TRow>
          <span>#</span><span>Date</span><span>Likes</span><span>Follows</span><span>Comments</span><span>Durée</span>
        </TRow>
        {sessions.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
            Aucune session TikTok enregistrée
          </div>
        ) : (
          sessions.slice(0, 20).map((sess, i) => (
            <TRow key={sess.id || i}>
              <span style={{ fontWeight: 600 }}>{i + 1}</span>
              <span>{new Date(sess.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
              <span style={{ color: '#ff0050', fontWeight: 600 }}>{sess.likes || 0}</span>
              <span style={{ color: '#00f2ea', fontWeight: 600 }}>{sess.follows || 0}</span>
              <span>{sess.comments || 0}</span>
              <span>{sess.duration_mins || 0} min</span>
            </TRow>
          ))
        )}
      </Table>
    </Page>
  );
};

export default TikTokAnalytics;
